import { Content } from '@prismicio/client';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import {
  ServicesSliceDefault,
  ServicesSliceGetStarted,
  ServicesSliceOurServices,
} from '../../../prismicio-types';
import { PlainRightArrow } from '@/assets/PlainRightArrow';
import { cn } from '@/lib/utils';

enum SLICE_VARIATION {
  default = 'default',
  getStarted = 'getStarted',
  ourServices = 'ourServices',
}
/**
 * Props for `Services`.
 */
export type ServicesProps = SliceComponentProps<Content.ServicesSlice>;

/**
 * Component for "Services" Slices.
 */

const Services = ({ slice }: ServicesProps): JSX.Element => {
  const getClassByVariation = (variation: string) => {
    switch (variation) {
      case SLICE_VARIATION.getStarted:
        return 'bg-[#f4f4f4]';
      case SLICE_VARIATION.ourServices:
        return 'bg-gradient-to-b from-white via-white via-70% to-[#B6F6EA]\n';
      default:
        return 'px-5 md:px-16 py-12 max-w-screen-2xl mx-auto';
    }
  };

  const styledClass = getClassByVariation(slice.variation);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={styledClass}
    >
      {slice.variation === SLICE_VARIATION.default && <Default slice={slice} />}
      {slice.variation === SLICE_VARIATION.getStarted && (
        <GetStarted slice={slice} />
      )}
      {slice.variation === SLICE_VARIATION.ourServices && (
        <OurServices slice={slice} />
      )}
    </section>
  );
};

const Default = ({ slice }: { slice: ServicesSliceDefault }): JSX.Element => {
  const {
    primary: { title, cards },
  } = slice;

  const numberOfCards = cards.length;

  return (
    <div className="text-white flex flex-col gap-y-20">
      <div className="w-full lg:w-2/3 mx-auto text-center">
        <PrismicRichText field={title} />
      </div>
      <div
        className={`grid grid-cols-1 ${numberOfCards === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3'} gap-12`}
      >
        {cards.map(
          (
            {
              image,
              title,
              description,
              button_label: buttonLabel,
              button_link: buttonLink,
            },
            index,
          ) => (
            <PrismicNextLink
              key={`card-${index.toString()}`}
              field={buttonLink}
            >
              <div className="flex flex-col gap-y-8 last:md:col-span-2 last:lg:col-span-1">
                <PrismicNextImage
                  field={image}
                  className={`rounded-[20px] ${numberOfCards === 2 ? 'h-[15rem] object-cover' : ''}`}
                />
                <PrismicRichText field={title} />
                <PrismicRichText field={description} />
                <button className="button-outline-white">
                  <span>{buttonLabel}</span>
                </button>
              </div>
            </PrismicNextLink>
          ),
        )}
      </div>
    </div>
  );
};

const GetStarted = ({
  slice,
}: {
  slice: ServicesSliceGetStarted;
}): JSX.Element => {
  const {
    primary: { title, description, cards },
  } = slice;

  const numberOfCards = cards.length;

  return (
    <div className="text-white max-w-screen-2xl mx-auto">
      <div className="w-full mx-auto flex flex-col md:flex-row">
        <div className="flex-1 space-y-6 px-5 py-16 md:px-16 md:py-12">
          <h1 className="text-2xl md:text-[40px] font-bold text-black leading-tight">
            <PrismicRichText field={title} />
          </h1>
          <p className="text-lg text-black">
            <PrismicRichText field={description} />
          </p>
          <div
            className={`grid grid-cols-1 ${numberOfCards === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3'} gap-12`}
          >
            {cards.map(
              (
                {
                  image,
                  title,
                  description,
                  button_label: buttonLabel,
                  button_link: buttonLink,
                },
                index,
              ) => (
                <PrismicNextLink
                  key={`card-${index.toString()}`}
                  field={buttonLink}
                >
                  <div className="text-black bg-white rounded-lg shadow-sm border h-[480px] border-gray-100 p-6 flex flex-col gap-y-8 last:md:col-span-2 last:lg:col-span-1 ">
                    <PrismicNextImage
                      field={image}
                      className={`rounded-[20px] ${numberOfCards === 2 ? 'h-[15rem] object-cover' : ''}`}
                    />
                    <div className="text-2xl font-bold leading-relaxed">
                      <PrismicRichText field={title} />
                    </div>
                    <div className="text-[16px] space-y-2">
                      <PrismicRichText field={description} />
                    </div>

                    <div className="mt-auto pt-2">
                      <button className="flex items-center rounded-lg gap-2 px-4 py-2 bg-black text-white font-bold hover:bg-black/80 transition-colors">
                        {buttonLabel}
                        <PlainRightArrow
                          arrowFill="white"
                          className="w-5 h-5"
                        />
                      </button>
                    </div>
                  </div>
                </PrismicNextLink>
              ),
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const OurServices = ({
  slice,
}: {
  slice: ServicesSliceOurServices;
}): JSX.Element => {
  const {
    primary: { title, description, cards },
  } = slice;

  const numberOfCards = cards.length;

  return (
    <div className="text-white max-w-screen-2xl mx-auto">
      <div className="w-full mx-auto flex flex-col md:flex-row">
        <div className="flex-1 space-y-6 px-5 py-16 md:px-16 md:py-12">
          <h1 className="text-2xl md:text-[40px] font-bold text-black leading-tight">
            <PrismicRichText field={title} />
          </h1>
          <p className="text-lg text-black">
            <PrismicRichText field={description} />
          </p>
          <div
            className={`grid grid-cols-1 ${numberOfCards === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3'} gap-12`}
          >
            {cards.map(
              (
                {
                  image,
                  title,
                  description,
                  button_label: buttonLabel,
                  button_link: buttonLink,
                },
                index,
              ) => {
                const borderColorClasses = [
                  'border-[#D96058]',
                  'border-[#7BC0E3]',
                  'border-[#FBBE36]',
                ];
                const bgColor =
                  borderColorClasses[index % borderColorClasses.length];

                return (
                  <PrismicNextLink
                    key={`card-${index.toString()}`}
                    field={buttonLink}
                  >
                    <div className="text-black bg-white rounded-lg shadow-sm border h-auto lg:max-h-[380px] md:max-h-[480px] md:h-[380px] border-gray-100 p-6 flex flex-col gap-y-8 last:md:col-span-2 last:lg:col-span-1 ">
                      <PrismicNextImage
                        field={image}
                        className={`rounded-[20px] ${numberOfCards === 2 ? 'h-[15rem] object-cover' : ''}`}
                      />
                      <div
                        className={cn(
                          'text-2xl font-bold leading-relaxed border-b-4 borderColor pb-6',
                          bgColor,
                        )}
                      >
                        <PrismicRichText field={title} />
                      </div>
                      <div className="text-[16px] space-y-2">
                        <PrismicRichText field={description} />
                      </div>

                      <div className="mt-auto pt-2">
                        <button className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-2xl font-bold hover:bg-black/80 transition-colors">
                          {buttonLabel}
                          <PlainRightArrow
                            arrowFill="white"
                            className="w-5 h-5"
                          />
                        </button>
                      </div>
                    </div>
                  </PrismicNextLink>
                );
              },
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Services;
